import { useScope } from "@state/scopeContext.lib";
import { useCallback, useMemo } from "react";
import { ZodObject, ZodRawShape, ZodSchema } from "zod";
import { useUserStatus } from "./useUserStatus";

export enum ORGANIZATION_USER_CACHE_KEYS {
    CLIENTS_PAGE_VIEW_CACHE_KEY = "clients-page-base-view-cache",
    CLIENTS_PAGE_BASE_VIEW_ID_CACHE_KEY = "clients-page-base-view-id-cache",
    CLIENTS_CLOSES_PAGE_VIEW_CACHE_KEY = "clients-closes-page-base-view-cache",
    CLIENTS_CLOSES_PAGE_BASE_VIEW_ID_CACHE_KEY = "clients-closes-page-base-view-id-cache",
    CLIENTS_WORK_PAGE_VIEW_CACHE_KEY = "clients-work-page-base-view-cache",
    CLIENTS_WORK_PAGE_BASE_VIEW_ID_CACHE_KEY = "clients-work-page-base-view-id-cache",
    CLIENTS_WORK_PAGE_BOARD_CACHE_KEY = "clients-work-page-board-cache",
    CLIENTS_TASKS_PAGE_FILTERS_CACHE_KEY = "clients-tasks-page-filters-cache",
    CLIENTS_TASKS_PAGE_VIEW_CACHE_KEY = "clients-tasks-page-view-cache",
    CLIENTS_TASKS_PAGE_DETAIL_VIEW_CACHE_KEY = "clients-tasks-page-detail-view-cache",
    CLIENTS_TASKS_PAGE_BASE_VIEW_ID_CACHE_KEY = "clients-tasks-page-base-view-id-cache",
    CLIENTS_TASKS_PAGE_BOARD_CACHE_KEY = "clients-tasks-page-board-cache",
    TEAM_PAGE_VIEW_CACHE_KEY = "team-page-base-view-cache",
    ORGANIZATION_INTEGRATIONS_PAGE_VIEW_CACHE_KEY = "organization-integrations-page-view-cache",
    ORGANIZATION_MEMBER_ACCESS_LOG_PAGE_VIEW_CACHE_KEY = "organization-member-access-log-page-view-cache",
    ORGANIZATION_ACTIVITY_PAGE_VIEW_CACHE_KEY = "organization-activity-page-view-cache-v2",
    ORGANIZATION_ACTIVITY_LAST_VISIT_CACHE_KEY = "organization-activity-last-visit-cache",
    ORGANIZATION_ACTIVITY_CHART_DENSITY_CACHE_KEY = "activity-chart-density",
    AGENT_USAGE_PAGE_VIEW_CACHE_KEY = "agent-usage-page-view-cache",
    PINNED_DATE_PRESETS_AGENT_USAGE_CACHE_KEY = "pinned-date-presets-agent-usage-cache",
    RECENT_WORKSPACES_CACHE_KEY = "recent-workspaces-cache",
    OPENING_BALANCES_CTA_DISMISSED_DATES = "opening-balances-cta-dismissed-dates",
    ACCOUNTANTS_TRAINING_CTA_DISMISSED_DATES = "accountants-training-cta-dismissed-dates",
    PINNED_DATE_PRESETS_PROFIT_LOSS_CACHE_KEY = "pinned-date-presets-profit-loss-cache",
    PINNED_DATE_PRESETS_BALANCE_SHEET_CACHE_KEY = "pinned-date-presets-balance-sheet-cache",
    PINNED_DATE_PRESETS_BALANCE_SHEET_TASK_CACHE_KEY = "pinned-date-presets-balance-sheet-task-cache",
    PINNED_DATE_PRESETS_GENERAL_LEDGER_CACHE_KEY = "pinned-date-presets-general-ledger-cache",
    PINNED_DATE_PRESETS_TRIAL_BALANCE_CACHE_KEY = "pinned-date-presets-trial-balance-cache",
    PINNED_DATE_PRESETS_SCHEDULES_REPORT_CACHE_KEY = "pinned-date-presets-schedules-report-cache",
    PINNED_DATE_PRESETS_CASH_FLOW_STATEMENT_CACHE_KEY = "pinned-date-presets-cash-flow-statement-cache",
    PINNED_DATE_PRESETS_EXPENSE_BY_VENDOR_CACHE_KEY = "pinned-date-presets-expense-by-vendor-cache",
    PINNED_DATE_PRESETS_INSIGHTS_CASH_FLOW_CACHE_KEY = "pinned-date-presets-insights-cash-flow-cache",
    PINNED_DATE_PRESETS_REPORT_PACKAGE_MODAL_CACHE_KEY = "pinned-date-presets-report-package-modal-cache",
    PINNED_DATE_PRESETS_INVOICING_CACHE_KEY = "pinned-date-presets-invoicing-cache",
    PINNED_DATE_PRESETS_BILL_PAY_CACHE_KEY = "pinned-date-presets-bill-pay-cache",
    PINNED_DATE_PRESETS_TASKS_CACHE_KEY = "pinned-date-presets-tasks-cache",
    PINNED_DATE_PRESETS_ELIMINATION_EXCEPTIONS_CACHE_KEY = "pinned-date-presets-elimination-exceptions-cache",
    PINNED_DATE_PRESETS_INTERCOMPANY_TRANSACTIONS_CACHE_KEY = "pinned-date-presets-intercompany-transactions-cache",
    INTERCOMPANY_TRANSACTIONS_PAGE_VIEW_CACHE_KEY = "intercompany-transactions-page-view-cache",
    PINNED_DATE_PRESETS_GL_DRILLDOWN_CACHE_KEY = "pinned-date-presets-gl-drilldown-cache",
    PINNED_DATE_PRESETS_ACCOUNTING_PAGE_CACHE_KEY = "pinned-date-presets-accounting-page-cache",
    PINNED_DATE_PRESETS_CHART_OF_ACCOUNTS_CACHE_KEY = "pinned-date-presets-chart-of-accounts-cache",
    PINNED_DATE_PRESETS_ELIMINATION_DETAIL_CACHE_KEY = "pinned-date-presets-elimination-detail-cache",
    PINNED_DATE_PRESETS_CASH_FLOW_DRILLDOWN_CACHE_KEY = "pinned-date-presets-cash-flow-drilldown-cache",
    PINNED_DATE_PRESETS_PROFIT_LOSS_TASK_CACHE_KEY = "pinned-date-presets-profit-loss-task-cache",
    PINNED_DATE_PRESETS_HISTORY_CACHE_KEY = "pinned-date-presets-history-cache",
}

/**
 * This cache is organization scoped, and is cleared on every login and logout.
 * WARNING: This hook only reads value from localStorage on mount, it won't refresh for already mounted component.
 * @param key Cache key that will be used to create the final key which contains the organizationId and the user id as well
 * @param zodSchema If specified, the data returned from the cache is going to be parsed. When parsing fails, undefined is returned
 * @returns
 */
export function useOrganizationUserCache<T = unknown>(
    key?: ORGANIZATION_USER_CACHE_KEYS,
    zodSchema?: ZodSchema<T> | ZodObject<ZodRawShape>,
): [T | undefined, (value: T | undefined) => void] {
    const { activeOrganizationKey } = useScope();
    const { data: userStatus } = useUserStatus();
    const userId = userStatus?.user.id;
    const finalKey = `${activeOrganizationKey}/${key}-${userId}`;

    const value = useMemo<T>(() => {
        if (!key || userId === undefined) {
            return undefined;
        }

        let rawValue: string | null;
        try {
            rawValue = localStorage.getItem(finalKey);
        } catch {
            return undefined;
        }

        if (!rawValue) {
            return undefined;
        }

        let parsedValue;
        try {
            parsedValue = JSON.parse(rawValue);
        } catch {
            return undefined;
        }

        if (zodSchema) {
            if (zodSchema.safeParse(parsedValue).success) {
                parsedValue = zodSchema.parse(parsedValue);
            } else {
                parsedValue = undefined;
            }
        }

        return parsedValue;
    }, [finalKey, zodSchema, key, userId]);

    const setter = useCallback(
        (v: T | undefined) => {
            if (!key || userId === undefined) {
                return;
            }
            try {
                if (v === undefined) {
                    localStorage.removeItem(finalKey);
                } else {
                    localStorage.setItem(finalKey, JSON.stringify(v));
                }
            } catch {
                return;
            }
        },
        [finalKey, key, userId],
    );

    return [value, setter];
}
